import { BoxSlotTypeOf } from "@/modules/boxSlot/boxSlotTypes"
import type { ProgramCalendarEventType } from "@/modules/calendar/calendarTypes"
import { ProgramType } from "@/modules/programs/types/programTypes"
import { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import { convertItemProgramSessionsToEvent } from "@/modules/sessions/programSessionTypes"
import { TrainingItemEntityFactory } from "@/modules/training/entities/trainingContentEntity"
import type { TrainingItemDto } from "@/modules/training/gateway/trainingDto"
import { useTrainingProgramFilter } from "@/modules/training/stores/useTrainingProgramFilterStore"
import { convertItemTrainingSessionsToEvent } from "@/modules/training/trainingTypes"
import { useUser } from "@/modules/users/hooks/useUser"
import { useServicesContext } from "@modules/core/services/services.context"
import { UserProgramItemEntity } from "@modules/programs/entities/userProgramItemEntity"
import { type TrainingCalendarEventType } from "@modules/training/components/trainingCalendarEvent"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import dayjs from "dayjs"

export const useTrainingSessionsToday = () => {
  const { trainingApi } = useServicesContext()

  const { data: trainingToday, isLoading } = useQuery({
    queryKey: ["trainingToday"],
    queryFn: () => trainingApi.getTrainingContentDate(dayjs().toString()),
    staleTime: 5 * 60 * 1000, // 5 min
  })

  return {
    trainingToday: trainingToday
      ? trainingToday.map((trainingContent: TrainingItemDto) => TrainingItemEntityFactory.fromDto(trainingContent))
      : [],
    isLoading,
  }
}

export const useProgramSelected = (programId: string) => {
  const selectedPrograms = useTrainingProgramFilter()
  return selectedPrograms.some((program) => program.id === programId)
}

export const useAllProgramsSelected = (programs: UserProgramItemEntity[]) => {
  const selectedPrograms = useTrainingProgramFilter()
  return programs.every((program) => selectedPrograms.some((p) => p.id === program.id))
}

export const useProgramSessionEvents = ({
  programId,
  programType,
  startDate,
  endDate,
}: {
  programId: string | undefined
  programType: ProgramType.VIRTUAL | ProgramType.PRO | ProgramType.ONOFF | undefined
  startDate: string | number | undefined
  endDate: string | number | undefined
}) => {
  const user = useUser()
  const { programInfiniteSessionApi, programOnOffSessionApi } = useServicesContext()
  if (typeof startDate === "string") startDate = dayjs(startDate).format("YYYY-MM-DD")
  if (typeof endDate === "string") endDate = dayjs(endDate).format("YYYY-MM-DD")
  const { data, isRefetching, isPending, refetch } = useSuspenseQuery<{
    events: ProgramCalendarEventType[]
    sessions: ProgramInfiniteSessionEntity[] | ProgramOnOffSessionEntity[]
  }>({
    queryKey: ["programSessions", programId, programType, startDate, endDate, user.id],
    queryFn: async () => {
      if (!programId) return { events: [], sessions: [] }
      if (!programType) return { events: [], sessions: [] }
      if (typeof startDate === "undefined") return { events: [], sessions: [] }
      if (typeof endDate === "undefined") return { events: [], sessions: [] }

      let sessions: ProgramInfiniteSessionEntity[] | ProgramOnOffSessionEntity[] = []

      if (programType === ProgramType.VIRTUAL || programType === ProgramType.PRO) {
        if (typeof startDate === "string" && typeof endDate === "string") {
          const result = await programInfiniteSessionApi.getSessions(startDate, endDate, programId)
          sessions = result.map((session) => ProgramInfiniteSessionEntity.fromDto(session))
        } else {
          throw new Error("Start date and end date must be of string type")
        }
      } else if (programType === ProgramType.ONOFF) {
        if (typeof startDate === "number" && typeof endDate === "number") {
          const result = await programOnOffSessionApi.getSessionsByIndex(startDate, endDate, programId)
          sessions = result.map((session) => ProgramOnOffSessionEntity.fromDto(session))
        } else if (typeof startDate === "string" && typeof endDate === "string") {
          const result = await programOnOffSessionApi.getSessions(startDate, endDate, programId)
          sessions = result.map((session) => ProgramOnOffSessionEntity.fromDto(session))
        } else {
          throw new Error("Start date and end date must be of the same type")
        }
      }

      return { events: convertItemProgramSessionsToEvent(sessions, user.id), sessions }
    },
    // enabled: !!programId && !!programType && typeof startDate !== "undefined" && typeof endDate !== "undefined",
  })
  return { data, isLoading: isRefetching || isPending, refetch }
}

export const usePlanningSessionEvents = (startDate?: string, endDate?: string) => {
  const user = useUser()
  const selectedPrograms = useTrainingProgramFilter()

  const { trainingApi } = useServicesContext()

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["trainingSessions", startDate, endDate],
    queryFn: () => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return trainingApi.getTrainingContents(startDate!, endDate!)
    },
    enabled: !!startDate && !!endDate,
  })

  return {
    data: data
      ? convertItemTrainingSessionsToEvent(
          data
            .map((trainingContent: TrainingItemDto) => TrainingItemEntityFactory.fromDto(trainingContent))
            .filter(
              (trainingContent) =>
                trainingContent.type === BoxSlotTypeOf ||
                selectedPrograms.some((p) => p.id === trainingContent.content.programId),
            ),
          user.id,
        )
      : ([] as TrainingCalendarEventType[]),
    isLoading,
    refetch,
    fetch,
  }
}
